/**
 * W66.G — AddNodeModal
 * Modal to add a new person to the org map.
 */

import { useState } from "react";
import { X } from "lucide-react";
import type { OrgNode, RoleInDeal, Sentiment } from "./types";
import { ROLES, SENTIMENTS } from "./types";

interface Props {
  accountId: number;
  nodes: OrgNode[];
  onSave: (data: Partial<OrgNode>) => Promise<void>;
  onClose: () => void;
}

export default function AddNodeModal({ nodes, onSave, onClose }: Props) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [role, setRole] = useState<RoleInDeal>("Unknown");
  const [power, setPower] = useState(3);
  const [sentiment, setSentiment] = useState<Sentiment>("Unknown");
  const [reportsTo, setReportsTo] = useState<number | "">("");
  const [notes, setNotes] = useState("");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) {
      setError("Name is required.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onSave({
        display_name: name.trim(),
        title: title.trim() || null,
        role_in_deal: role,
        power_level: power,
        sentiment,
        reports_to_node_id: reportsTo !== "" ? Number(reportsTo) : null,
        notes: notes.trim() || null,
      });
      onClose();
    } catch (e: any) {
      setError(e?.message ?? "Save failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      data-testid="add-node-modal"
    >
      <div className="bg-background border border-border rounded-lg shadow-xl w-full max-w-lg">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="text-[15px] font-semibold tracking-tight">Add person</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && (
            <div className="px-3 py-2 rounded text-[12px] bg-rose-50 border border-rose-200 text-rose-800">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block md:col-span-2">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Name *</span>
              <input
                autoFocus
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="mt-1 w-full text-[13px] px-2.5 py-1.5 rounded border border-border bg-background focus:outline-none focus:border-foreground"
                data-testid="add-node-name"
              />
            </label>

            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Title</span>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="mt-1 w-full text-[13px] px-2.5 py-1.5 rounded border border-border bg-background focus:outline-none focus:border-foreground"
                data-testid="add-node-title"
              />
            </label>

            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Role in Deal</span>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as RoleInDeal)}
                className="mt-1 w-full text-[13px] px-2.5 py-1.5 rounded border border-border bg-background focus:outline-none focus:border-foreground"
                data-testid="add-node-role"
              >
                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </label>

            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Sentiment</span>
              <select
                value={sentiment}
                onChange={(e) => setSentiment(e.target.value as Sentiment)}
                className="mt-1 w-full text-[13px] px-2.5 py-1.5 rounded border border-border bg-background focus:outline-none focus:border-foreground"
                data-testid="add-node-sentiment"
              >
                {SENTIMENTS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>

            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Power Level (1–5)</span>
              <input
                type="number"
                min={1}
                max={5}
                value={power}
                onChange={(e) => setPower(Math.max(1, Math.min(5, Number(e.target.value))))}
                className="mt-1 w-full text-[13px] px-2.5 py-1.5 rounded border border-border bg-background focus:outline-none focus:border-foreground"
                data-testid="add-node-power"
              />
            </label>

            {nodes.length > 0 && (
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Reports To</span>
                <select
                  value={reportsTo}
                  onChange={(e) =>
                    setReportsTo(e.target.value === "" ? "" : Number(e.target.value))
                  }
                  className="mt-1 w-full text-[13px] px-2.5 py-1.5 rounded border border-border bg-background focus:outline-none focus:border-foreground"
                  data-testid="add-node-reports-to"
                >
                  <option value="">— none —</option>
                  {nodes.map((n) => (
                    <option key={n.id} value={n.id}>
                      {n.display_name}
                    </option>
                  ))}
                </select>
              </label>
            )}

            <label className="block md:col-span-2">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Notes</span>
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional context..."
                className="mt-1 w-full text-[13px] px-2.5 py-1.5 rounded border border-border bg-background focus:outline-none focus:border-foreground"
                data-testid="add-node-notes"
              />
            </label>
          </div>

          <div className="flex items-center gap-2 pt-2 border-t border-border">
            <button
              type="submit"
              disabled={busy || !name.trim()}
              className="text-[12px] px-4 py-1.5 rounded border bg-foreground text-background border-foreground hover:opacity-90 disabled:opacity-50 transition"
              data-testid="add-node-submit"
            >
              {busy ? "Saving..." : "Save"}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="text-[12px] px-4 py-1.5 rounded border border-border bg-background hover:bg-[hsl(var(--surface-2))] transition"
              data-testid="add-node-cancel"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
